import type { FormData } from '../types/form';
import { DOCUMENT_CAPABILITIES, type SelectOption } from './referenceData';
export interface PermissionMappingResult {
  permissions: Record<string, unknown>;
  opsNotes: Record<string, string>;
}
function labelFor(options: SelectOption[], id: string): string {
  return options.find(o => o.value === id)?.label || id;
}
function labelsFor(options: SelectOption[], ids: string[]): string[] {
  return ids.map(id => labelFor(options, id));
}
function parseCommaList(value: string): string[] {
  return value.split(',').map(s => s.trim()).filter(Boolean);
}
export function mapFormToPermissions(form: FormData): PermissionMappingResult {
  const permissions: Record<string, unknown> = {};
  const opsNotes: Record<string, string> = {};
  if (form.needSubgroups !== null) {
    permissions.allowAddSubgroupWidget = form.needSubgroups;
  }
  if (form.needSubgroups && form.subgroupLevels !== null) {
    permissions.groupChildrenDepth = form.subgroupLevels;
  }
  if (form.numberOfUsers !== '') {
    permissions.numberOfUsers = form.numberOfUsers;
  }
  if (!form.documentAllTags && form.documentTemplateTagIds.length > 0) {
    permissions.documentCenterTags = form.documentTemplateTagIds.map(Number);
  } else if (form.documentAllTags) {
    permissions.documentCenterTags = [];
  }
  if (!form.documentAllServices && form.documentServiceIds.length > 0) {
    permissions.documentCenterService = form.documentServiceIds.map(Number);
  } else if (form.documentAllServices) {
    permissions.documentCenterService = [];
  }
  for (const cap of DOCUMENT_CAPABILITIES) {
    if (cap.permission) {
      permissions[cap.permission] = form.documentCapabilities.includes(cap.key);
    }
  }
  if (form.documentCapabilities.includes('create')) {
    opsNotes.documentCreation = 'Requested — configure via Document Hub access';
  }
  if (form.docsPerMonth !== '') permissions.numberOfDocumentsPerMonth = form.docsPerMonth;
  if (form.docsInProgress !== '') permissions.numberOfDocumentsCanWorkOn = form.docsInProgress;
  if (form.maxPagesPerReview !== '') {
    permissions.legalDocumentReviewPagesNumbers = form.maxPagesPerReview;
  }
  if (form.monthlyShares !== '') permissions.monthlyShares = form.monthlyShares;
  if (form.monthlyDownloads !== '') permissions.monthlyDownloads = form.monthlyDownloads;
  if (form.monthlyReviews !== '') permissions.monthlyReviews = form.monthlyReviews;
  if (form.hasLegalAICopilot !== null) {
    permissions.AskMeAnything = form.hasLegalAICopilot;
  }
  if (form.hasLegalAICopilot) {
    permissions.chatAiProvinces = form.copilotAllProvinces ? [] : form.copilotProvinceIds.map(Number);
    permissions.chatAiServices = form.copilotAllPracticeAreas ? [] : form.copilotPracticeAreaIds.map(Number);
    if (form.copilotMonthlyLimit !== '') {
      permissions.numberOfQuestionsMonthly = form.copilotMonthlyLimit;
    }
    permissions.ChatHistory = form.chatHistory;
    permissions.ContinuePreviousConversations = form.continuePreviousConversations;
  }
  if (form.hasHelpline !== null) {
    permissions.legalHelpLine = form.hasHelpline;
  }
  if (form.hasHelpline) {
    permissions.phoneNumberMask = form.helplineMaskNumber;
    permissions.phoneNumber = form.helplineRealNumber;
    if (form.helplinePrimaryEmail.trim()) {
      permissions.legalLineEmail = form.helplinePrimaryEmail.trim();
    }
    if (form.hasAccessToIntakeForm !== null) {
      permissions.hasAccessToIntakeForm = form.hasAccessToIntakeForm;
    }
    if (form.helplineEmails.length > 0) {
      opsNotes.helplineAdditionalContacts = JSON.stringify(form.helplineEmails);
    }
  } else if (form.hasHelpline === false) {
    permissions.hasAccessToIntakeForm = false;
  }
  if (form.hasGetAQuote !== null) {
    permissions.allowGetQuote = form.hasGetAQuote;
  }
  if (form.hasGetAQuote) {
    permissions.MarketplaceLawyers = form.marketplaceLawyerIds.map(Number);
    if (form.marketplaceDiscount !== '') {
      permissions.discountPercentageQuotes = form.marketplaceDiscount;
    }
  }
  permissions.legalInquiryForm = form.hasLegalInquiryForm;
  if (form.claimsSubmit !== null) permissions.claimsSubmit = form.claimsSubmit;
  if (form.claimsEdit !== null) permissions.claimsEdit = form.claimsEdit;
  if (form.claimsChangeStatus !== null) {
    permissions.claimsChangeStatus = form.claimsChangeStatus;
  }
  if (form.claimsLawyerIds.length > 0) {
    permissions.claimsLawyers = form.claimsLawyerIds.map(Number);
  }
  if (form.hasKnowledgeCenter !== null) {
    permissions.knowledgeCenterEnable = form.hasKnowledgeCenter;
  }
  if (form.hasKnowledgeCenter) {
    const categories = parseCommaList(form.knowledgeCenterCategoriesInput);
    if (categories.length > 0) {
      permissions.knowledgeBaseCategories = categories;
    } else {
      opsNotes.knowledgeBaseCategories = 'Use admin platform defaults';
    }
  }
  if (form.supportAccess !== null) permissions.supportAccess = form.supportAccess;
  if (form.supportAccess) {
    const categories = parseCommaList(form.supportCategoriesInput);
    if (categories.length > 0) {
      permissions.supportCategoryOverride = categories;
    } else {
      opsNotes.supportCategories = 'Use admin platform defaults';
    }
    permissions.onlyGroupAdminsCanSubmitTicket = form.onlyGroupAdminsCanSubmitTicket;
  }
  permissions.skipPhoneVerification = form.skipPhoneVerification;
  permissions.canUseForgotPassword = form.canUseForgotPassword;
  permissions.viewSubscriptionPlans = form.viewSubscriptionPlans;
  permissions.showBillingInformation = form.showBillingInformation;
  if (form.otherNotes.trim()) opsNotes.additionalNotes = form.otherNotes;
  return {
    permissions,
    opsNotes
  };
}
export function formatPermissionMappingForEmail(mapping: PermissionMappingResult, refs: {
  provinces: SelectOption[];
  serviceCategories: SelectOption[];
  lawyers: SelectOption[];
  documentTags: SelectOption[];
  documentServices: SelectOption[];
}): string {
  const lines: string[] = [];
  const p = mapping.permissions;
  const add = (label: string, value: unknown) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value) && value.length === 0) {
      lines.push(`<tr><td style="padding:4px 8px;color:#7e8299;">${label}</td><td style="padding:4px 8px;"><em>All / none</em></td></tr>`);
      return;
    }
    lines.push(`<tr><td style="padding:4px 8px;color:#7e8299;vertical-align:top;">${label}</td><td style="padding:4px 8px;font-family:monospace;font-size:12px;">${String(value)}</td></tr>`);
  };
  let html = `<table width="100%" cellpadding="0" cellspacing="0" style="margin-top:16px;border:1px dashed #E8EEF4;">`;
  html += `<tr><td colspan="2" style="padding:8px 12px;background:#F6F9FC;font-size:13px;"><strong>Technical Permission Mapping (for ops)</strong></td></tr>`;
  Object.entries(p).forEach(([key, value]) => {
    if (key === 'chatAiProvinces' && Array.isArray(value)) {
      add('chatAiProvinces', value.length ? labelsFor(refs.provinces, value.map(String)).join(', ') : 'All provinces');
    } else if (key === 'chatAiServices' && Array.isArray(value)) {
      add('chatAiServices', value.length ? labelsFor(refs.serviceCategories, value.map(String)).join(', ') : 'All practice areas');
    } else if (key === 'documentCenterTags' && Array.isArray(value)) {
      add('documentCenterTags', value.length ? labelsFor(refs.documentTags, value.map(String)).join(', ') : 'All template tags');
    } else if (key === 'documentCenterService' && Array.isArray(value)) {
      add('documentCenterService', value.length ? labelsFor(refs.documentServices, value.map(String)).join(', ') : 'All services');
    } else if ((key === 'claimsLawyers' || key === 'MarketplaceLawyers') && Array.isArray(value)) {
      add(key, labelsFor(refs.lawyers, value.map(String)).join(', '));
    } else if (key === 'knowledgeBaseCategories' || key === 'supportCategoryOverride') {
      add(key, (value as string[]).join(', '));
    } else {
      add(key, typeof value === 'boolean' ? value ? 'true' : 'false' : value);
    }
  });
  Object.entries(mapping.opsNotes).forEach(([key, value]) => {
    add(`[note] ${key}`, value);
  });
  html += lines.join('');
  html += `</table>`;
  return html;
}